/**
 * RunningBalanceOverlay — computed column and summary bar for transaction arrays.
 *
 * Declared via x-overlay: "running_balance" on an array schema node.
 * Requires parent data with begin_balance + end_balance, and items with amount.
 */

import { formatCurrency } from '../../../utils/formatters';

/**
 * Compute running balance for each row.
 * Returns array of balance values, one per row.
 */
export function computeRunningBalances(parentData, rows) {
  const beginBal = parseFloat(parentData?.begin_balance) || 0;
  const balances = [];
  let running = beginBal;
  for (const row of rows) {
    running += parseFloat(row.amount) || 0;
    balances.push(Math.round(running * 100) / 100);
  }
  return balances;
}

/**
 * Compute balance summary for the overlay header.
 */
export function computeBalanceSummary(parentData, rows) {
  const beginBal = parseFloat(parentData?.begin_balance) || 0;
  const endBal = parseFloat(parentData?.end_balance) || 0;
  const txnSum = rows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const computed = Math.round((beginBal + txnSum) * 100) / 100;
  const gap = Math.round((computed - endBal) * 100) / 100;

  return {
    beginBalance: beginBal,
    endBalance: endBal,
    computedEndBalance: computed,
    hasDiscrepancy: Math.abs(gap) >= 0.01,
    gap,
  };
}

/**
 * Render the balance summary bar as a React element.
 */
export function BalanceSummaryBar({ parentData, rows }) {
  const summary = computeBalanceSummary(parentData, rows);

  return (
    <div style={{
      display: 'flex', gap: '16px', padding: '6px 8px',
      background: 'var(--color-gray-50, #f9fafb)',
      borderBottom: '1px solid var(--color-gray-200, #e5e7eb)',
      fontSize: 'var(--text-sm, 13px)',
      flexWrap: 'wrap',
    }}>
      <span>Begin: <strong>{formatCurrency(summary.beginBalance)}</strong></span>
      <span>End (scanned): <strong>{formatCurrency(summary.endBalance)}</strong></span>
      <span>End (computed): <strong style={{
        color: summary.hasDiscrepancy ? 'var(--color-error, #dc2626)' : 'var(--color-success, #16a34a)',
      }}>{formatCurrency(summary.computedEndBalance)}</strong></span>
      {summary.hasDiscrepancy && (
        <span style={{ color: 'var(--color-error, #dc2626)', fontWeight: 600 }}>
          Gap: {formatCurrency(summary.gap)}
        </span>
      )}
    </div>
  );
}

/**
 * Overlay registry entry for running_balance.
 */
export const runningBalanceOverlay = {
  id: 'running_balance',
  SummaryComponent: BalanceSummaryBar,
  computeColumn: computeRunningBalances,
  columnLabel: 'Balance',
  columnFormat: 'currency',
};
